import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Pie } from 'react-chartjs-2';
import { DataContext } from '../contexts/DataContext';

import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend
);

const ShipmentsByConsignorPieChart = () => {
    const { shipments, isLoading } = useContext(DataContext);
    const [pieData, setPieData] = useState({
        labels: [],
        datasets: [{
            data: [],
            backgroundColor: [],
            hoverOffset: 4
        }],
    });
    const [consignorTableData, setConsignorTableData] = useState([]);

    useEffect(() => {
        if (!isLoading && shipments) {
            const consignorCounts = shipments.reduce((acc, shipment) => {
                const consignor = shipment.consignorName || 'Unknown';
                acc[consignor] = (acc[consignor] || 0) + 1;
                return acc;
            }, {});

            // Create an array from the entries, sort them, and then separate them back out
            const sortedConsignorData = Object.entries(consignorCounts)
                .sort((a, b) => b[1] - a[1])  // Sort by count descending
                .reduce((acc, [key, value]) => {
                    acc.labels.push(key);
                    acc.data.push(value);
                    return acc;
                }, { labels: [], data: [] });

            const backgroundColors = sortedConsignorData.labels.map(() =>
                `rgba(37, 99, 235, ${Math.random() * 0.5 + 0.5})`  // You may want to standardize these colors
            );

            setPieData({
                labels: sortedConsignorData.labels,
                datasets: [{
                    data: sortedConsignorData.data,
                    backgroundColor: backgroundColors,
                    hoverOffset: 4
                }],
            });

            setConsignorTableData(sortedConsignorData.labels.map((label, index) => ({
                name: label,
                count: sortedConsignorData.data[index]
            })));
        }
    }, [shipments, isLoading]);


    const options = {
        plugins: {
            legend: {
                display: false, // Disable the legend entirely
            },
            tooltip: {
                enabled: true // Ensure tooltips are still enabled
            },
            datalabels: {
                display: false // Confirm data labels are still disabled
            }
        },
        maintainAspectRatio: false
    };
    

    const renderConsignorTable = () => {
        return (
            <div className="mt-4 overflow-y-auto" style={{ maxHeight: "200px" }}>
                <table className="w-full text-sm bg-white shadow rounded-lg overflow-hidden">
                    <thead className="bg-blue-600 text-white">
                        <tr>
                            <th className="px-4 py-2">Consignor</th>
                            <th className="px-4 py-2">Shipments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {consignorTableData.map(({ name, count }) => (
                            <tr key={name} className="border-b">
                                <td className="px-4 py-2">{name}</td>
                                <td className="px-4 py-2">{count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };
    

    return (
        <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg flex flex-col justify-between h-custom-card-big overflow-y-auto">
            <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-800 flex items-center">
                    <FontAwesomeIcon icon={faChartPie} className="text-blue-600 mr-2" />
                    Shipments by Consignor
                </h2>
            </div>
            <div className="relative h-96">
                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
                        <span className="ml-2 text-lg">Loading...</span>
                    </div>
                ) : (
                    <Pie data={pieData} options={options} />
                )}
            </div>
            {renderConsignorTable()}
        </div>
    );
    
};

export default ShipmentsByConsignorPieChart;
